import type { ResponseWithParsedData } from '@libs/core';

export type ApiResponse<T> = T | Error | ResponseWithParsedData<T>;

export const unwrapApiResponseOrThrow = <T>(response: ApiResponse<T>, errorMessage = 'Request failed') => {
    if (!response) {
        throw new Error(errorMessage);
    }

    if (response instanceof Error) {
        throw response;
    }

    // A non-success status comes back as the raw fetch `Response` with the error body attached at
    // `parsedResponse` (see apiFetch `handleResponseCode`). The top-level `detail` check below misses
    // it because `detail` is nested, so handle it explicitly and surface the nested detail.
    if (response instanceof Response && !response.ok) {
        const { parsedResponse } = response;

        if (
            parsedResponse &&
            typeof parsedResponse === 'object' &&
            'detail' in parsedResponse &&
            typeof (parsedResponse as { detail: unknown }).detail === 'string'
        ) {
            throw new Error((parsedResponse as { detail: string }).detail);
        }

        throw new Error(errorMessage);
    }

    if (typeof response === 'object') {
        if ('detail' in response && typeof response.detail === 'string') throw new Error(response.detail);
        if ('parsedResponse' in response) return response.parsedResponse;
    }

    return response;
};
